import React from 'react';
import { Calendar, MapPin } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';
import UpcomingEventSection from './home/UpcomingEventSection';
import CheerJamboreeSection from './home/CheerJamboreeSection';
import CheerJamboreeFlyer from './jamboree/CheerJamboreeFlyer';
import PanthersClassicFlyer from './jamboree/PanthersClassicFlyer';
import JamboreeInterestForm from './jamboree/JamboreeInterestForm';
import JamboreeCoachContacts from './jamboree/JamboreeCoachContacts';
import { useUpcomingEvent } from '../hooks/useUpcomingEvent';

const STRIP_STYLE = {
  background: 'linear-gradient(90deg, #000 0%, #1a1300 50%, #000 100%)',
  borderBottom: '2px solid #FFD700',
  padding: '0.85rem 1rem',
  color: '#fff',
};

const STRIP_INNER_STYLE = {
  maxWidth: '1100px',
  margin: '0 auto',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem 1.5rem',
  fontSize: '0.95rem',
  textAlign: 'center',
};

const FORM_SECTION_STYLE = {
  background: 'linear-gradient(180deg, #050505 0%, #1a1300 100%)',
  padding: '4rem 1rem',
  borderTop: '1px solid rgba(255, 215, 0, 0.18)',
};

const FORM_CONTAINER_STYLE = {
  maxWidth: '900px',
  margin: '0 auto',
};

const FORM_HEADER_STYLE = {
  textAlign: 'center',
  marginBottom: '2rem',
};

const FORM_PILL_STYLE = {
  display: 'inline-block',
  background: '#FFD700',
  color: '#000',
  padding: '0.35rem 0.95rem',
  borderRadius: '999px',
  fontWeight: 800,
  fontSize: '0.78rem',
  letterSpacing: '1px',
  marginBottom: '1rem',
};

const FORM_TITLE_STYLE = {
  fontSize: 'clamp(1.6rem, 3vw, 2.25rem)',
  color: '#FFD700',
  margin: '0 0 0.5rem 0',
  fontWeight: 800,
};

const FORM_CARD_STYLE = {
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 215, 0, 0.22)',
  borderRadius: '16px',
  padding: 'clamp(1.25rem, 3vw, 2rem)',
};

const JamboreePage = () => {
  const { event: upcomingEvent } = useUpcomingEvent();
  return (
  <div className="page-container" data-testid="jamboree-page">
    <Navigation />

    {/* Compact event strip (in lieu of big hero, per design ask) */}
    <div style={STRIP_STYLE} data-testid="jamboree-event-strip">
      <div style={STRIP_INNER_STYLE}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          <Calendar size={16} style={{ color: '#FFD700' }} />
          <strong>{upcomingEvent.date}</strong>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          <MapPin size={16} style={{ color: '#FFD700' }} />
          {upcomingEvent.venue} · {upcomingEvent.city}
        </span>
      </div>
    </div>

    <main className="main-content">
      {/* NEW: dynamic Panthers Classic flyer (always in sync with data) */}
      <section
        data-testid="football-flyer-section"
        style={{
          background: 'linear-gradient(180deg, #050505 0%, #0d0a00 100%)',
          padding: '4rem 1rem 2.5rem',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <span
            style={{
              display: 'inline-block',
              background: '#FFD700',
              color: '#000',
              padding: '0.35rem 0.95rem',
              borderRadius: '999px',
              fontWeight: 800,
              fontSize: '0.78rem',
              letterSpacing: '1px',
              marginBottom: '0.75rem',
            }}
          >
            🏈 PANTHERS CLASSIC · SHAREABLE FLYER
          </span>
          <p style={{ color: '#d4d4d4', maxWidth: '640px', margin: '0 auto', lineHeight: 1.6 }}>
            Save or screenshot the flyer below to share on Facebook, Instagram, or in your team group chats.
          </p>
        </div>
        <PanthersClassicFlyer />
      </section>

      <UpcomingEventSection showShare hideFlyerImage />

      {/* Dedicated Cheer Jamboree flyer (auto-generated, matches event content) */}
      <section
        data-testid="cheer-flyer-section"
        style={{
          background: 'linear-gradient(180deg, #050505 0%, #0d0a00 100%)',
          padding: '4rem 1rem',
          borderTop: '1px solid rgba(255, 215, 0, 0.18)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <span
            style={{
              display: 'inline-block',
              background: '#FFD700',
              color: '#000',
              padding: '0.35rem 0.95rem',
              borderRadius: '999px',
              fontWeight: 800,
              fontSize: '0.78rem',
              letterSpacing: '1px',
              marginBottom: '0.75rem',
            }}
          >
            CHEER FLYER · SHAREABLE
          </span>
          <p style={{ color: '#d4d4d4', maxWidth: '620px', margin: '0 auto', lineHeight: 1.6 }}>
            Save or screenshot the flyer below to share on Facebook, Instagram, or in your squad group chats.
          </p>
        </div>
        <CheerJamboreeFlyer />
      </section>

      <CheerJamboreeSection />

      <section style={FORM_SECTION_STYLE} data-testid="jamboree-interest-section">
        <div style={FORM_CONTAINER_STYLE}>
          <div style={FORM_HEADER_STYLE}>
            <span style={FORM_PILL_STYLE}>EXPRESS INTEREST</span>
            <h2 style={FORM_TITLE_STYLE}>Register Your Team or Squad</h2>
            <p style={{ color: '#d4d4d4', maxWidth: '640px', margin: '0 auto', lineHeight: 1.6 }}>
              Tell us a little about your team or squad and a Panthers coach will follow up with
              registration details, age divisions, and game-day logistics. Prefer a direct line?
              Reach the coaches below.
            </p>
          </div>

          <JamboreeCoachContacts />

          <div style={FORM_CARD_STYLE}>
            <JamboreeInterestForm />
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
  );
};

export default JamboreePage;
